import { Box, styled } from '@mui/material';
import { FailureRemediationPlanResponse } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { pluralize } from '@/utils/format';
import { RemediationCopy } from './remediationCopy';

const FileProblemList = styled('ul')({
  margin: '4px 0 0',
  padding: '0 0 0 20px',
  maxHeight: '160px',
  overflowY: 'auto',
});

const Note = ({ testId, text }: { testId: string; text: string }) => (
  <BodyText color='text.secondary' display='block' data-testid={testId}>
    {text}
  </BodyText>
);

export const RemediationPlanBreakdown = ({
  plan,
  copy,
}: {
  plan: FailureRemediationPlanResponse;
  copy: RemediationCopy;
}) => {
  const fileProblems = plan.candidates.filter(candidate => candidate.verdict === 'file-problem');
  const unclassified = plan.candidates.filter(candidate => candidate.verdict === 'unclassified').length;

  return (
    <Box display='flex' flexDirection='column' gap='12px' mt={1}>
      {fileProblems.length > 0 && (
        <Box data-testid='remediation-file-problems'>
          <BodyText color='text.secondary' display='block'>
            {`Not queued: ${pluralize(fileProblems.length, 'failure is', 'failures are')} a problem with the file itself, so a ${copy.verb} would fail the same way.`}
          </BodyText>
          <FileProblemList>
            {fileProblems.map(candidate => (
              <li key={candidate.id}>
                <BodyText color='text.secondary'>{`${candidate.name}: ${candidate.reason ?? 'file problem'}`}</BodyText>
              </li>
            ))}
          </FileProblemList>
        </Box>
      )}
      {unclassified > 0 && (
        <Note
          testId='remediation-unclassified'
          text={`Included: ${pluralize(unclassified, 'failure', 'failures')} could not be classified, because no log entry was found or log search is not set up here. A ${copy.verb} is the only way to find out whether they recover.`}
        />
      )}
      {plan.alreadyTried > 0 && (
        <Note
          testId='remediation-already-tried'
          text={`Left out: ${pluralize(plan.alreadyTried, 'failure', 'failures')} already failed again after a ${copy.verb}. Retry those one at a time from their rows.`}
        />
      )}
      {plan.totalMatching > plan.candidates.length && (
        <Note
          testId='remediation-batch-limit'
          text={`This covers the ${plan.candidates.length} most recent of ${pluralize(plan.totalMatching, copy.itemSingular, copy.itemPlural)}. Run it again for the rest.`}
        />
      )}
    </Box>
  );
};
